// ── participant-capstone-collection: own submission rows (no report column) ───

import { useMemo } from "react";
import {
  Badge,
  useCollection,
  type CollectionColumnDef,
  type UseCollectionResult
} from "@vjal/ui";

import type { CoachingCapstoneSubmissionDto } from "../../services/coaching-readiness-dtos";
import { capstoneStatusLabel, capstoneStatusTone } from "../capstone/capstone-helpers";

export const PARTICIPANT_CAPSTONE_PAGE_SIZE = 25;
export const PARTICIPANT_CAPSTONE_DEFAULT_SORT = { id: "createdAt", dir: "desc" as const };

export type ParticipantCapstoneRow = CoachingCapstoneSubmissionDto & Record<string, unknown>;

export function buildParticipantCapstoneCollectionColumns(): CollectionColumnDef<ParticipantCapstoneRow>[] {
  return [
    {
      id: "status",
      header: "Status",
      sortable: true,
      sortAccessor: (row) => row.status ?? "",
      priority: 1,
      cell: (row) => (
        <Badge variant={capstoneStatusTone(row.status) === "danger" ? "danger" : capstoneStatusTone(row.status)}>
          {capstoneStatusLabel(row.status)}
        </Badge>
      )
    },
    {
      id: "createdAt",
      header: "Submitted",
      sortable: true,
      sortAccessor: (row) => row.createdAt ?? "",
      priority: 2,
      cell: (row) => <span className="text-sm text-ink-muted">{row.createdAt ?? "—"}</span>
    }
  ];
}

export function useParticipantCapstoneCollection(
  rows: ParticipantCapstoneRow[]
): UseCollectionResult<ParticipantCapstoneRow> {
  const columns = useMemo(() => buildParticipantCapstoneCollectionColumns(), []);

  return useCollection<ParticipantCapstoneRow>({
    mode: "client",
    rows,
    columns,
    pageSize: PARTICIPANT_CAPSTONE_PAGE_SIZE,
    urlSync: false,
    initialState: { sort: PARTICIPANT_CAPSTONE_DEFAULT_SORT },
    getRowKey: (row) => row.id
  });
}